import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./Select.module.css";
import { getClassName } from "../../utils/className.js";

/** Smallest the always-visible custom thumb is allowed to shrink to, in px. */
const MIN_THUMB_SIZE = 20;

/**
 * @typedef {Object} SelectOption
 * @property {string | number} value - The unique value of the option.
 * @property {string} label - The display text of the option.
 * @property {boolean} [disabled] - Whether the option is disabled.
 * @property {React.ReactNode} [icon] - Optional icon/element rendered before the label (e.g. a flag).
 */

/**
 * SelectMenu renders the dropdown options list in a portal or inline.
 * Handles loading status, scrolling indicators, list empty state, and single/multi option configurations.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the menu is currently visible.
 * @param {React.RefObject<HTMLDivElement>} props.menuRef - Reference to the menu container element.
 * @param {React.RefObject<HTMLUListElement>} props.listRef - Reference to the UL options list element.
 * @param {Object} props.classes - Custom inner CSS classes overrides.
 * @param {string} [props.classes.menu] - Override class for the menu.
 * @param {string} [props.classes.option] - Override class for options.
 * @param {boolean} props.menuPortal - If true, portals the element to dialog or body.
 * @param {Object} [props.menuPos] - The coordinates and dimensions of the parent wrapper.
 * @param {number} props.menuPos.top - Top absolute coordinate.
 * @param {number} props.menuPos.left - Left absolute coordinate.
 * @param {number} props.menuPos.width - Target width.
 * @param {HTMLElement | null} props.portalTarget - DOM element target for portaling.
 * @param {boolean} [props.isLoading] - Displays primary loading options indicator (async mode).
 * @param {Array<SelectOption>} props.displayedOptions - Filtered lists of options to render.
 * @param {Array<string | number>} props.selectedValues - Collection of currently selected option values.
 * @param {number} props.focusedIndex - List item index focused via keyboard arrows.
 * @param {(value: string | number) => void} props.handleSelect - Selection callback.
 * @param {(index: number) => void} props.setFocusedIndex - Selection highlight focus index change handler.
 * @param {(e: React.UIEvent<HTMLUListElement>) => void} props.handleScroll - Scroll listener to invoke loadMore.
 * @param {boolean} [props.isLoadingMore] - Renders loader item at bottom of list.
 * @param {boolean} [props.hasMore] - Shows indicator at list bottom to scroll more.
 * @param {boolean} [props.multiple] - If true, formats and displays option elements as multi-select checkboxes.
 * @param {string | number} [props.limitWidth] - The max-width of the menu in menu portal mode. Defaults to "450px".
 * @param {string | number} [props.maxMenuWidth] - Limits the max-width of the dropdown menu in both portal and non-portal modes.
 */
const SelectMenu = ({
  isOpen,
  menuRef,
  listRef,
  classes = {},
  menuPortal,
  menuPos,
  portalTarget,
  isLoading = false,
  displayedOptions,
  selectedValues,
  focusedIndex,
  handleSelect,
  setFocusedIndex,
  handleScroll,
  isLoadingMore = false,
  hasMore = false,
  multiple = false,
  menuSearch = false,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Type to search...",
  limitWidth = "450px",
  maxMenuWidth,
  maxMenuHeight,
  minMenuWidth,
}) => {
  // Custom always-visible scroll thumb — native scrollbars are hidden (see
  // Select.module.css) because macOS Chrome/Safari overlay scrollbars only
  // paint on hover/scroll regardless of ::-webkit-scrollbar styling.
  const [thumbMetrics, setThumbMetrics] = useState(null);

  const updateThumb = () => {
    const el = listRef.current;
    if (!el) {
      setThumbMetrics(null);
      return;
    }
    const { scrollHeight, clientHeight, scrollTop } = el;
    if (scrollHeight <= clientHeight + 1) {
      setThumbMetrics(null);
      return;
    }
    const thumbHeight = Math.max(MIN_THUMB_SIZE, (clientHeight / scrollHeight) * clientHeight);
    const scrollableDistance = scrollHeight - clientHeight;
    const maxTop = clientHeight - thumbHeight;
    const top = scrollableDistance > 0 ? (scrollTop / scrollableDistance) * maxTop : 0;
    setThumbMetrics({ top, height: thumbHeight });
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    updateThumb();
    const el = listRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(updateThumb);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, displayedOptions.length, isLoading, isLoadingMore]);

  const onListScroll = (e) => {
    updateThumb();
    handleScroll && handleScroll(e);
  };

  if (!isOpen) {
    return null;
  }

  const toCss = (v) => (typeof v === "number" ? `${v}px` : v);
  const limitWidthStyle = toCss(limitWidth);
  const maxMenuHeightStyle = toCss(maxMenuHeight);
  const minMenuWidthStyle = toCss(minMenuWidth);

  // A caller-supplied cap wins over the viewport-derived one so a long option
  // list stays a usable size; the CSS var drives the scrolling list inside.
  const heightStyle = maxMenuHeightStyle
    ? { maxHeight: maxMenuHeightStyle, "--select-menu-max-height": maxMenuHeightStyle }
    : null;

  const menuNode = (
    <div
      ref={menuRef}
      className={getClassName(styles.menu, "select-menu", classes.menu)}
      style={
        menuPortal && menuPos
          ? {
            position: "fixed",
            top: menuPos.top,
            bottom: menuPos.bottom,
            left: menuPos.left,
            // Never narrower than the trigger, but a caller can widen it.
            minWidth: minMenuWidthStyle
              ? `max(${menuPos.width}px, ${minMenuWidthStyle})`
              : menuPos.width,
            width: "max-content",
            maxWidth: `max(${menuPos.width}px, ${limitWidthStyle})`,
            maxHeight: menuPos.maxHeight != null ? `${menuPos.maxHeight}px` : undefined,
            margin: 0,
            zIndex: 9999,
            transition: menuPos.enableTransition ? "top 0.25s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1)" : "none",
            ...(menuPos.maxHeight ? { '--select-menu-max-height': `${menuPos.maxHeight}px` } : {}),
            ...heightStyle,
          }
          : {
            ...(minMenuWidthStyle ? { minWidth: minMenuWidthStyle } : {}),
            ...heightStyle,
          }
      }
    >
      {menuSearch && (
        <div className={getClassName(styles.menuSearchContainer, "select-menu-search-container")}>
          <svg className={getClassName(styles.menuSearchIcon, "select-menu-search-icon")} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
          <input
            type="text"
            className={getClassName(styles.menuSearchInput, "select-menu-search-input")}
            value={searchValue}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.key === "Escape" && e.stopPropagation()}
          />
        </div>
      )}
      {isLoading ? (
        <div className={getClassName(styles.loadingContainer, "select-loading-container")}>
          <span className={getClassName(styles.spinner, "select-spinner")} />
          <span>Loading options...</span>
        </div>
      ) : displayedOptions.length === 0 ? (
        <div className={getClassName(styles.noOptions, "select-no-options")}>No results found</div>
      ) : (
        <div className={getClassName(styles.optionsListWrapper, "select-options-list-wrapper")}>
          <ul
            ref={listRef}
            className={getClassName(styles.optionsList, "select-options-list", classes.optionsList)}
            role="listbox"
            tabIndex={-1}
            onScroll={onListScroll}
          >
            {displayedOptions.map((opt, idx) => {
              const isSelected = selectedValues.includes(opt.value);
              const isHovered = focusedIndex === idx;

              const optionClassName = getClassName(
                styles.option,
                "select-option",
                {
                  [styles.multiOption]: multiple,
                  "select-multi-option": multiple,
                  [styles.optionSelected]: isSelected,
                  "select-option-selected": isSelected,
                  [styles.optionHovered]: isHovered,
                  "select-option-hovered": isHovered,
                  [styles.optionDisabled]: opt.disabled,
                  "select-option-disabled": opt.disabled,
                },
                classes.option,
              );

              return (
                <li
                  key={opt.value}
                  className={optionClassName}
                  style={maxMenuWidth != null ? { maxWidth: typeof maxMenuWidth === 'number' ? `${maxMenuWidth}px` : maxMenuWidth } : undefined}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={opt.disabled}
                  onClick={() => !opt.disabled && handleSelect(opt.value)}
                  onMouseEnter={() => !opt.disabled && setFocusedIndex(idx)}
                >
                  {multiple ? (
                    <>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={opt.disabled}
                        readOnly
                        className={getClassName(styles.optionCheckbox, "select-option-checkbox")}
                      />
                      {opt.icon && (
                        <span className={getClassName(styles.optionIcon, "select-option-icon")}>{opt.icon}</span>
                      )}
                      <span className={getClassName(styles.optionLabel, "select-option-label")}>{opt.label}</span>
                    </>
                  ) : (
                    <>
                      {opt.icon && (
                        <span className={getClassName(styles.optionIcon, "select-option-icon")}>{opt.icon}</span>
                      )}
                      <span className={getClassName(styles.optionLabel, "select-option-label")}>{opt.label}</span>
                    </>
                  )}
                </li>
              );
            })}
            {(isLoadingMore || hasMore) && (
              <li className={getClassName(styles.loadingMoreContainer, "select-loading-more-container")}>
                <span className={getClassName(styles.spinner, "select-spinner")} />
                <span>{isLoadingMore ? "Loading more..." : "Scroll to load more"}</span>
              </li>
            )}
          </ul>
          {thumbMetrics && (
            <div className={getClassName(styles.scrollThumbTrack, "select-scroll-thumb-track")}>
              <div
                className={getClassName(styles.scrollThumb, "select-scroll-thumb")}
                style={{ top: thumbMetrics.top, height: thumbMetrics.height }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  return menuPortal && portalTarget ? createPortal(menuNode, portalTarget) : menuNode;
};

export default SelectMenu;
